import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Send, Plus, Trash2, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PASSWORD = process.env.REACT_APP_CLIENT_FORM_PASSWORD || "";

export default function NewClientForm() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    client: "",
    contact: "",
    payment_notes: "",
    misc_scope: "",
    pages: [{ page: "", scope_of_work: "", price: "" }]
  });

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError("");
    } else {
      setPasswordError("Incorrect password");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (index, field, value) => {
    const newPages = [...formData.pages];
    newPages[index][field] = value;
    setFormData(prev => ({ ...prev, pages: newPages }));
  };

  const addPage = () => {
    setFormData(prev => ({
      ...prev,
      pages: [...prev.pages, { page: "", scope_of_work: "", price: "" }]
    }));
  };

  const removePage = (index) => {
    if (formData.pages.length > 1) {
      const newPages = formData.pages.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, pages: newPages }));
    }
  };

  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/client/submit`, formData);
      
      if (response.data.success) {
        console.log(`Box.com folder ID: ${response.data.box_folder_id} (${response.data.box_folder})`);
        setShowSuccess(true);
        toast.success(response.data.message || "Client documents created successfully!");
      } else {
        // Handle case where API returns success: false with error details
        console.error("Document processing error:", response.data.error);
        toast.error(response.data.message || "Document processing failed. Please try again.");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to submit form. Please try again.";
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  // Success screen after submission
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 py-20 px-6 flex items-center justify-center">
        <Card className="bg-white border border-gray-200 shadow-sm max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="font-heading text-2xl text-gray-900 mb-4">Client Forms Are Being Created</h2>
            <p className="text-gray-600 mb-6">
              The contract, invoice, and W9 documents are being generated and uploaded to Box.com. 
              A new folder has been created for this client.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
              <p className="text-blue-800 text-sm">
                <strong>Documents being created:</strong>
              </p>
              <ul className="text-blue-700 text-sm mt-2 space-y-1">
                <li>• Contract (Master Services Agreement)</li>
                <li>• Invoice</li>
                <li>• W9 Form</li>
              </ul>
            </div>
            <Button
              onClick={() => navigate('/')}
              className="bg-accent text-black hover:bg-accent/90 font-semibold"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Password protection screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 py-20 px-6 flex items-center justify-center">
        <Card className="bg-white border border-gray-200 shadow-sm max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <Lock className="w-12 h-12 text-accent mx-auto mb-4" />
              <h1 className="font-heading text-2xl text-gray-900">Access Protected</h1>
              <p className="text-gray-600 mt-2">Enter the password to access the client registration form</p>
            </div>
            
            <form onSubmit={handlePasswordSubmit}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="password" className="text-gray-700">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    className="mt-2 bg-white border-gray-300 text-gray-900"
                    placeholder="Enter password"
                    autoFocus
                  />
                  {passwordError && (
                    <p className="text-red-500 text-sm mt-2">{passwordError}</p>
                  )}
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-accent text-black hover:bg-accent/90 font-semibold"
                >
                  Access Form
                </Button>
              </div>
            </form>
            
            <div className="mt-6 text-center">
              <Link to="/" className="text-gray-500 hover:text-accent text-sm">
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20 px-6">
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-8">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-accent mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <div className="flex items-center gap-4 mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
            alt="Ethical Wiki" 
            className="w-12 h-12"
            width="48"
            height="48"
          />
          <div>
            <h1 className="font-heading text-3xl text-gray-900">New Client Registration</h1>
            <p className="text-gray-600">Internal form for new client onboarding</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit}>
              <CardHeader className="p-0 mb-8">
                <CardTitle className="font-heading text-2xl text-gray-900">Client Information</CardTitle>
                <CardDescription className="text-gray-600">
                  Enter the client details and service scope
                </CardDescription>
              </CardHeader>
              
              <div className="space-y-6">
                {/* Client and Contact */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client" className="text-gray-700">Client *</Label>
                    <Input
                      id="client"
                      name="client"
                      value={formData.client}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                      placeholder="Client name or company"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contact" className="text-gray-700">Contact *</Label>
                    <Input
                      id="contact"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent"
                      placeholder="Contact person or email"
                      required
                    />
                  </div>
                </div>
                
                {/* Payment Notes */}
                <div>
                  <Label htmlFor="payment_notes" className="text-gray-700">Payment Notes</Label>
                  <Textarea
                    id="payment_notes"
                    name="payment_notes"
                    value={formData.payment_notes}
                    onChange={handleChange}
                    className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent min-h-[80px]"
                    placeholder="Payment terms, schedule, or special arrangements"
                  />
                </div>
                
                {/* Misc Scope */}
                <div>
                  <Label htmlFor="misc_scope" className="text-gray-700">Misc Scope</Label>
                  <Textarea
                    id="misc_scope"
                    name="misc_scope"
                    value={formData.misc_scope}
                    onChange={handleChange}
                    className="mt-2 bg-white border-gray-300 text-gray-900 focus:border-accent focus:ring-accent min-h-[80px]"
                    placeholder="Additional scope notes or miscellaneous details"
                  />
                </div>
                
                {/* Pages Section */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-xl text-gray-900">Pages & Scope of Work</h3>
                    <Button
                      type="button"
                      onClick={addPage}
                      variant="outline"
                      className="border-accent text-accent hover:bg-accent hover:text-black"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Page
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    {formData.pages.map((pageItem, index) => (
                      <div key={index} className="grid grid-cols-12 gap-3 items-start p-4 bg-gray-50 rounded-md">
                        <div className="col-span-4">
                          {index === 0 && <Label className="text-gray-700 text-sm mb-1 block">Page</Label>}
                          <Input
                            value={pageItem.page}
                            onChange={(e) => handlePageChange(index, 'page', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            placeholder="Page"
                          />
                        </div>
                        
                        <div className="col-span-5">
                          {index === 0 && <Label className="text-gray-700 text-sm mb-1 block">Scope of Work</Label>}
                          <Select 
                            value={pageItem.scope_of_work}
                            onValueChange={(value) => handlePageChange(index, 'scope_of_work', value)}
                          >
                            <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                              <SelectValue placeholder="Select scope" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                              <SelectItem value="content_development">Content Development</SelectItem>
                              <SelectItem value="maintenance_subscription">Maintenance Subscription</SelectItem>
                              <SelectItem value="other_wiki_services">Other Wiki Services</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="col-span-2">
                          {index === 0 && <Label className="text-gray-700 text-sm mb-1 block">Price</Label>}
                          <Input
                            value={pageItem.price}
                            onChange={(e) => handlePageChange(index, 'price', e.target.value)}
                            className="bg-white border-gray-300 text-gray-900"
                            placeholder="$0"
                          />
                        </div>
                        
                        <div className="col-span-1 flex items-end">
                          {index === 0 && <div className="h-6"></div>}
                          <Button
                            type="button"
                            onClick={() => removePage(index)}
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2"
                            disabled={formData.pages.length === 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-accent text-black hover:bg-accent/90 font-semibold mt-6"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Creating..." : "Create Documents"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
